import React, { useState } from 'react';
import {
    MapPin,
    ChevronLeft,
    Upload,
    CheckCircle2,
    AlertCircle,
    RefreshCw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/hooks/useAuth';
import { useKYC } from '@/shared/hooks/useKYC';
import { cn } from '@/shared/lib/utils';
import { uploadKYCImage } from '@/shared/utils/uploadImage';

const AddressVerificationPage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { submitApplication } = useKYC(user?.uid);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [address, setAddress] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!selectedFile || !address) return;

        setIsSubmitting(true);
        try {
            // Upload image to Firebase Storage (60초 타임아웃)
            const uploadTimeout = new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('업로드 시간이 초과되었습니다. 네트워크를 확인하고 다시 시도해주세요.')), 60000)
            );

            const imageUrl = await Promise.race([
                uploadKYCImage(selectedFile, user!.uid, 'ADDRESS'),
                uploadTimeout
            ]);

            // Submit application with real image URL
            const result = await submitApplication('ADDRESS', {
                address,
                fileName: selectedFile.name,
                fileSize: selectedFile.size,
                fileType: selectedFile.type,
                imageUrl: imageUrl
            });

            if (result.success) {
                alert('거주지 증명이 제출되었습니다. 관리자 승인 후 반영됩니다.');
                navigate('/verification');
            } else {
                alert('제출 중 오류가 발생했습니다. 다시 시도해주세요.');
            }
        } catch (error) {
            console.error('Image upload error:', error);
            const msg = error instanceof Error ? error.message : '이미지 업로드 중 오류가 발생했습니다.';
            alert(msg + '\n다시 시도해주세요.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto space-y-8 animate-in fade-in duration-700">
            <button
                onClick={() => navigate('/verification')}
                className="flex items-center gap-2 text-slate-500 hover:text-white transition-colors text-sm font-bold group"
            >
                <ChevronLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" /> 뒤로가기
            </button>

            <div className="space-y-2">
                <h1 className="text-3xl font-black text-white flex items-center gap-3">
                    <MapPin className="h-8 w-8 text-emerald-500" /> 거주지 증명
                </h1>
                <p className="text-slate-500 font-bold text-sm">현재 거주 중인 주소와 이를 증명할 수 있는 서류를 업로드해주세요.</p>
            </div>

            <div className="rounded-[2.5rem] bg-[#0f0f12] border border-white/5 p-8 shadow-2xl space-y-8">
                <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest px-1">거주지 주소</label>
                    <input
                        type="text"
                        placeholder="도로명 주소 입력"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        className="w-full h-14 bg-white/5 border border-white/5 rounded-2xl px-6 text-sm font-bold text-white outline-none focus:border-emerald-500/50 transition-all"
                    />
                </div>

                <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest px-1">증빙 서류 (공과금 고지서 등)</label>
                    <div className="relative group h-48">
                        <input
                            type="file"
                            accept=".jpg,.png,.pdf"
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                            onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                    setSelectedFile(e.target.files[0]);
                                }
                            }}
                        />
                        <div className={cn(
                            "h-full border-2 border-dashed rounded-3xl flex flex-col items-center justify-center gap-4 transition-all duration-300",
                            selectedFile ? "border-emerald-500/50 bg-emerald-500/5" : "border-white/5 bg-white/[0.02] group-hover:border-emerald-500/30 group-hover:bg-emerald-500/5"
                        )}>
                            {selectedFile ? (
                                <>
                                    <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                                    <p className="text-sm font-black text-white">{selectedFile.name}</p>
                                </>
                            ) : (
                                <>
                                    <Upload className="h-8 w-8 text-slate-400" />
                                    <p className="text-sm font-black text-white">서류 업로드</p>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className="p-6 rounded-3xl bg-emerald-500/5 border border-emerald-500/10 flex items-start gap-4">
                    <AlertCircle className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                        <p className="text-xs font-black text-emerald-500">인정 범위</p>
                        <p className="text-[10px] font-bold text-slate-500 leading-relaxed">
                            최근 3개월 이내 발급된 주민등록등본, 공과금 고지서(전기, 가스, 수도), 통신비 납부 확인서 등
                        </p>
                    </div>
                </div>

                <button
                    onClick={handleSubmit}
                    disabled={!selectedFile || !address || isSubmitting}
                    className="w-full h-16 rounded-2xl bg-emerald-600 text-white font-black shadow-xl shadow-emerald-600/20 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all uppercase tracking-widest text-sm flex items-center justify-center gap-3"
                >
                    {isSubmitting ? <RefreshCw className="h-5 w-5 animate-spin" /> : "거주지 증명 제출하기"}
                </button>
            </div>
        </div>
    );
};

export default AddressVerificationPage;
